// Rappel de saisie des heures, sans IA : chacun choisit d'être rappelé (backend/app/services/hours_reminder.py).

import React, { useEffect, useState } from 'react';
import { Bell, BellOff, CheckCircle, Loader2 } from 'lucide-react';
import { apiService } from '../../services/api';

const DEFAULT_TIMES = { evening: '18:00', morning: '09:30' };

const formatHours = (value) => `${String(value ?? 7).replace('.', ',')} h`;

const HoursReminder = () => {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.getHoursReminder()
      .then(setSettings)
      .catch((err) => setError(err.message || 'Rappel indisponible.'));
  }, []);

  const save = async (changes) => {
    const next = { ...settings, ...changes };
    setSettings(next);
    setStatus('saving');
    setError(null);
    try {
      const saved = await apiService.updateHoursReminder({
        enabled: next.enabled,
        moment: next.moment,
        time: next.time,
      });
      setSettings(saved);
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(err.message || "Impossible d'enregistrer le rappel.");
    }
  };

  if (!settings) {
    return error ? <p className="mb-4 text-xs text-red-600">{error}</p> : null;
  }

  const Icon = settings.enabled ? Bell : BellOff;

  return (
    <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <label className="flex cursor-pointer items-center gap-2 font-medium text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={settings.enabled}
            onChange={(e) => save({ enabled: e.target.checked })}
          />
          <Icon className={`h-4 w-4 ${settings.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
          Me rappeler mes heures
        </label>
        {settings.enabled && (
          <div className="flex items-center gap-2 text-gray-700">
            <select
              aria-label="Moment du rappel"
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={settings.moment}
              onChange={(e) => save({ moment: e.target.value, time: DEFAULT_TIMES[e.target.value] })}
            >
              <option value="evening">le soir même</option>
              <option value="morning">le lendemain matin</option>
            </select>
            <span>à</span>
            <input
              type="time"
              aria-label="Heure du rappel"
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={settings.time}
              onChange={(e) => setSettings({ ...settings, time: e.target.value })}
              onBlur={(e) => e.target.value && save({ time: e.target.value })}
            />
          </div>
        )}
        {status === 'saving' && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        {status === 'saved' && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle className="h-3.5 w-3.5" /> Enregistré
          </span>
        )}
      </div>
      {settings.enabled && (
        <p className="mt-1 text-xs text-gray-500">
          {settings.moment === 'morning'
            ? 'Le matin, Deepflow regarde ton dernier jour travaillé (le vendredi pour un lundi)'
            : 'Le soir, Deepflow regarde ta journée'}
          {' '}: s'il manque des heures pour atteindre {formatHours(settings.expected_hours)}, il te le rappelle sur Slack,
          ou dans la cloche si tu es sur Deepflow. Rien les jours non travaillés, en congé ou fériés.
        </p>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default HoursReminder;
